import {
  SETTINGS_LEVELS_ORDER,
  STANDS_PAYMENT_TENDER_SETTINGS,
  TIP_CONFIGURATIONS_SETTINGS,
  TIP_OPTIONS_SETTINGS_NAMES,
} from 'resources'
import { fillArrayWithNullToLength } from 'utils/fillArrayWithNullToLength'
import settingsUtils from 'utils/settings'

/**
 * Retrieves all stand settings names
 * Is is used to filter all settings and return settings for stands.
 *
 * @returns {Array} - The array of all stand settings names.
 */

export const getAllStandsSettingsNames = () => {
  const tipOptionsSettingsNames = TIP_OPTIONS_SETTINGS_NAMES

  const paymentsTendersSettingsNames =
    STANDS_PAYMENT_TENDER_SETTINGS.map((setting) => setting.name)

  const tipConfigurationsSettingsNames =
    TIP_CONFIGURATIONS_SETTINGS.map((setting) => setting.name)

  const settingsNames = [
    ...paymentsTendersSettingsNames,
    ...tipConfigurationsSettingsNames,
    ...tipOptionsSettingsNames,
  ]

  return settingsNames
}

/**
 * Retrieves and transforms stand settings based on provided parameters,
 * ensuring tip_values defaults to [null, null, null].
 *
 * @param {Array} settings - The array of settings to be processed.
 * @param {Array} defaultSettings - The default settings array.
 * @param {Object} currentRevenueCenter - The current revenue center object.
 * @param {string} venueUuid - The UUID of the venue.
 * @param {string} orgName - The name of the organization.
 * @returns {Object} stand settings object.
 */

export const getTransformedStandsSettings = (
  settings,
  defaultSettings,
  currentRevenueCenter,
  venueUuid,
  orgName
) => {
  const settingsNames = getAllStandsSettingsNames()

  const standSettings = settingsUtils.determineValidSettingsLevel({
    settings: settings,
    defaultSettings: defaultSettings,
    standId: currentRevenueCenter?.id,
    venueUuid: venueUuid,
    organizationName: orgName,
    settingsNames,
    extraFilters: {
      configurationModelId: null,
      deactivatedAt: null,
    },
    level: SETTINGS_LEVELS_ORDER.STAND,
  })

  const transformedStandSettings =
    settingsUtils?.convertSettingsToObject(standSettings)

  return (
    {
      ...transformedStandSettings,
      // ensure tip_values to have a length of 4 to match the length of the tip options
      tip_values: fillArrayWithNullToLength(
        transformedStandSettings?.tip_values ?? [],
        4
      ),
    } ?? {}
  )
}

/**
 * Add parameter isVisibleInSpybar to the provided settings objects based on provide_tip_suggestions value.
 *
 * @param {Array} settings
 * @param {Boolean} provide_tip_suggestions
 * @param {Array} sectionsToUpdate
 * @returns {Array}
 */

export const updateSettingsVisibility = (
  settings,
  provide_tip_suggestions,
  sectionsToUpdate
) =>
  settings?.map((setting) => {
    const isVisibleInSpybar = provide_tip_suggestions

    if (sectionsToUpdate.includes(setting.text)) {
      return {
        ...setting,
        isVisibleInSpybar: isVisibleInSpybar,
      }
    }

    return setting
  })

export const revenueCentersUtils = {
  getAllStandsSettingsNames,
  getTransformedStandsSettings,
  updateSettingsVisibility,
}
